/**
 * Permission Helper
 *
 * Extracts permissions from JWT token and maps them to menu structure.
 * This prevents privilege escalation attacks by deriving all UI permissions
 * from the cryptographically signed JWT token instead of tamperable localStorage.
 */

import { MenuModel } from './_models'

interface JWTPayload {
  permissions?: string[]
  exp?: number
  sub?: string
  [key: string]: any
}

/**
 * Decodes a JWT token and extracts the payload
 * @param token - The JWT token string
 * @returns Decoded payload or null if invalid
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.')
    if (parts.length !== 3) {
      console.error('Invalid JWT format: token does not have 3 parts')
      return null
    }

    // Decode the payload (second part)
    const payload = parts[1]
    const decoded = atob(payload)
    const parsed = JSON.parse(decoded) as JWTPayload

    return parsed
  } catch (error) {
    console.error('Failed to decode JWT token:', error)
    return null
  }
}

/**
 * Checks if a specific permission exists in the permissions array
 * @param permissions - Array of permission strings from JWT
 * @param permission - Permission to check for
 * @returns True if permission exists
 */
const hasPermission = (permissions: string[], permission: string): boolean => {
  return permissions.includes(permission)
}

/**
 * Derives menu access structure from JWT token permissions
 * @param token - The JWT token string
 * @returns MenuModel object with permissions derived from token
 */
export const deriveMenuFromToken = (token: string): MenuModel => {
  // Default menu with all permissions set to false
  const defaultMenu: MenuModel = {
    claim: { view: false, export: false, edit: false, create: false },
    provider: { view: false, export: false, edit: false, create: false },
    icd: { view: false, export: false, edit: false, create: false },
    accountprovider: { view: false, export: false, edit: false, create: false },
    activationprovider: { view: false, export: false, edit: false, create: false },
  }

  // Decode the JWT token
  const payload = decodeJWT(token)
  if (!payload || !payload.permissions || !Array.isArray(payload.permissions)) {
    console.warn('No permissions found in JWT token, using default (empty) menu')
    return defaultMenu
  }

  const permissions = payload.permissions

  // Map JWT permissions to menu structure
  const menu: MenuModel = {
    claim: {
      view: hasPermission(permissions, 'ViewClaim'),
      export: hasPermission(permissions, 'ExportClaim'),
      edit: hasPermission(permissions, 'EditClaim'),
      create: hasPermission(permissions, 'CreateClaim'),
    },
    provider: {
      view: hasPermission(permissions, 'ViewProvider'),
      export: hasPermission(permissions, 'ExportProvider'),
      edit: hasPermission(permissions, 'EditProvider'),
      create: hasPermission(permissions, 'CreateProvider'),
    },
    icd: {
      view: hasPermission(permissions, 'ViewICD'),
      export: hasPermission(permissions, 'ExportICD'),
      edit: hasPermission(permissions, 'EditICD'),
      create: hasPermission(permissions, 'CreateICD'),
    },
    accountprovider: {
      view: hasPermission(permissions, 'ViewAccountProvider'),
      export: hasPermission(permissions, 'ExportAccountProvider'),
      edit: hasPermission(permissions, 'EditAccountProvider'),
      create: hasPermission(permissions, 'CreateAccountProvider'),
    },
    activationprovider: {
      view: hasPermission(permissions, 'ViewActivationProvider'),
      export: hasPermission(permissions, 'ExportActivationProvider'),
      edit: hasPermission(permissions, 'EditActivationProvider'),
      create: hasPermission(permissions, 'CreateActivationProvider'),
    },
  }

  return menu
}

/**
 * Validates if a JWT token is expired
 * @param token - The JWT token string
 * @returns True if token is expired or invalid
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token)
  if (!payload || !payload.exp) {
    return true
  }

  const currentTime = Math.floor(Date.now() / 1000)
  // Add 5 second buffer to avoid edge cases
  return payload.exp <= currentTime + 5
}

/**
 * Extracts user email from JWT token
 * @param token - The JWT token string
 * @returns User email or null
 */
export const getUserEmailFromToken = (token: string): string | null => {
  const payload = decodeJWT(token)
  if (!payload) {
    return null
  }

  // Try different claim formats
  return (
    payload.sub ||
    payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
    payload.email ||
    null
  )
}
